import React, { createContext, useContext, useState, useEffect } from "react";

export interface Tag {
  id: string;
  name: string;
}

export interface SelectedTag {
  tag: string;
  value: string;
}

interface TagContextType {
  availableTags: Tag[];
  addTag: (tagName: string) => boolean;
  updateTag: (tagId: string, newName: string) => boolean;
  deleteTag: (tagId: string) => void;
  setAvailableTags: React.Dispatch<React.SetStateAction<Tag[]>>;
}

const TagContext = createContext<TagContextType | undefined>(undefined);

export const TagProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);

  const addTag = (tagName: string): boolean => {
    console.log("TagContext: Adding tag:", tagName);

    // Check if tag already exists
    const tagExists = availableTags.some(
      (tag) => tag.name.toLowerCase() === tagName.toLowerCase()
    );

    if (tagExists) {
      console.log("TagContext: Tag already exists");
      return false; // Duplicate tag
    }

    if (availableTags.length >= 10) {
      console.log("TagContext: Maximum limit reached");
      return false; // Maximum limit reached
    }

    const newTag: Tag = {
      id: `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: tagName.trim(),
    };

    console.log("TagContext: Created new tag:", newTag);
    setAvailableTags((prev) => {
      const updated = [...prev, newTag];
      console.log("TagContext: Updated tags after add:", updated);
      return updated;
    });
    return true;
  };

  const updateTag = (tagId: string, newName: string): boolean => {
    console.log("TagContext: Updating tag:", tagId, "to:", newName);

    // Check if new name already exists in other tags
    const tagExists = availableTags.some(
      (tag) =>
        tag.id !== tagId && tag.name.toLowerCase() === newName.toLowerCase()
    );

    if (tagExists) {
      console.log("TagContext: Tag name already exists");
      return false; // Duplicate tag name
    }

    setAvailableTags((prev) => {
      const updated = prev.map((tag) =>
        tag.id === tagId ? { ...tag, name: newName.trim() } : tag
      );
      console.log("TagContext: Updated tags after edit:", updated);
      return updated;
    });
    return true;
  };

  const deleteTag = (tagId: string) => {
    console.log("TagContext: Deleting tag with ID:", tagId);
    console.log("TagContext: Current tags before deletion:", availableTags);

    const tagToDelete = availableTags.find((tag) => tag.id === tagId);
    console.log("TagContext: Tag to delete found:", tagToDelete);

    if (!tagToDelete) {
      console.error("TagContext: Tag not found for deletion!");
      return;
    }

    setAvailableTags((prev) => {
      console.log("TagContext: Previous tags in delete function:", prev);
      const updated = prev.filter((tag) => {
        const shouldKeep = tag.id !== tagId;
        console.log(
          `TagContext: Tag ${tag.id} (${tag.name}) - Keep: ${shouldKeep}`
        );
        return shouldKeep;
      });
      console.log("TagContext: Tags after deletion:", updated);
      console.log(
        "TagContext: Number of tags before:",
        prev.length,
        "after:",
        updated.length
      );
      return updated;
    });
  };

  // Debug: Log when availableTags changes
  useEffect(() => {
    console.log("TagContext: availableTags changed:", availableTags);
  }, [availableTags]);

  return (
    <TagContext.Provider
      value={{
        availableTags,
        addTag,
        updateTag,
        deleteTag,
        setAvailableTags,
      }}
    >
      {children}
    </TagContext.Provider>
  );
};

export const useTags = () => {
  const context = useContext(TagContext);
  if (context === undefined) {
    throw new Error("useTags must be used within a TagProvider");
  }
  return context;
};
